import { Box, Button, Chip, Stack, CircularProgress, Alert, Typography, Paper, Divider } from '@mui/material';
import { AutocompleteArrayInput } from 'react-admin';
import { FaSearch, FaTrashAlt } from 'react-icons/fa';
import { useMemo } from 'react';
import { useMemberDeletion } from '../../hooks/useMembersDeletion';

interface DeleteSectionProps {
	type: 'email' | 'phone';
	choices: Array<{ id: string; name: string }>;
	companyId: string;
	onSuccess: () => void;
}

export const DeleteSection = ({ type, choices, companyId, onSuccess }: DeleteSectionProps) => {
	const { selectedItems, isDeleting, error, setError, handleDelete, removeItem, updateItems } =
		useMemberDeletion<string>({
			companyId,
			onSuccess,
		});

	const selectedItemsSection = useMemo(() => {
		if (selectedItems.length === 0) return null;

		return (
			<Box
				sx={{
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 1,
					p: 2,
					backgroundColor: 'background.default',
				}}
			>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					{type === 'email' ? 'מיילים' : 'טלפונים'} שנבחרו:
				</Typography>
				<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
					{selectedItems.map((item: string) => (
						<Chip
							key={item}
							label={item}
							onDelete={() => !isDeleting && removeItem(item)}
							color="primary"
							variant="outlined"
							size="small"
							disabled={isDeleting}
							sx={{ borderRadius: '16px' }}
						/>
					))}
				</Stack>
			</Box>
		);
	}, [selectedItems, isDeleting, type, removeItem]);

	return (
		<Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
						{`הסרת משתמשים `}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{choices.length} {type === 'email' ? 'מיילים' : 'טלפונים'} זמינים
					</Typography>
				</Box>

				<Divider />

				{error && (
					<Alert
						severity="error"
						onClose={() => setError(null)}
						sx={{ '& .MuiAlert-message': { width: '100%' } }}
					>
						{error}
					</Alert>
				)}

				<Box sx={{ position: 'relative' }}>
					<Box sx={{ position: 'absolute', left: 8, top: 12, zIndex: 1, color: 'text.secondary' }}>
						<FaSearch />
					</Box>
					<AutocompleteArrayInput
						label={`חיפוש ${type === 'email' ? 'מיילים' : 'טלפונים'}`}
						fullWidth
						source={`${type}_to_remove`}
						choices={choices}
						defaultValue={selectedItems}
						onChange={(e) => updateItems(e || [])}
						disabled={isDeleting}
						helperText={`נבחרו ${selectedItems.length} ${type === 'email' ? 'מיילים' : 'טלפונים'}`}
						optionText="name"
						optionValue="id"
						suggestionLimit={100}
						createText="הוסף"
						clearOnBlur
						filterToQuery={(searchText) => ({ name: searchText })}
						sx={{
							'& .MuiInputBase-root': {
								paddingRight: '32px',
							},
							'& .MuiAutocomplete-tag': {
								margin: '2px',
								borderRadius: '16px',
							},
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
							},
							direction: 'rtl',
						}}
					/>
				</Box>

				{selectedItemsSection}

				<Box
					sx={{
						display: 'flex',
						gap: 2,
						justifyContent: 'flex-end',
						mt: 2,
						borderTop: '1px solid',
						borderColor: 'divider',
						pt: 2,
						direction: 'rtl',
					}}
				>
					<Button
						variant="outlined"
						disabled={isDeleting || selectedItems.length === 0}
						onClick={() => updateItems([])}
						size="large"
					>
						נקה הכל
					</Button>
					<Button
						variant="contained"
						color="error"
						disabled={isDeleting || selectedItems.length === 0}
						onClick={() => handleDelete(`delete_allowed_${type}s`)}
						startIcon={isDeleting ? <CircularProgress size={20} /> : <FaTrashAlt />}
						size="large"
						sx={{ display: 'flex', direction: 'ltr', alignItems: 'center', gap: 1 }}
					>
						{isDeleting ? 'מוחק...' : 'מחק נבחרים'}
					</Button>
				</Box>
			</Box>
		</Paper>
	);
};
